import {
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Badge,
  Box,
  Typography,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { setSelectUser, setMessage, setReplyMessage, setLeadWaDetails, resetMessagesState, clearChats } from "../messagesSlice";
import { formatLatestDate } from "../../../components/formatDateAndTime/formatDateAndTime";

// Predefined color palette
const avatarColors = [
  "#dc2626", "#db2777", "#9333ea", "#7c3aed",
  "#4f46e5", "#2563eb", "#0284c7", "#0891b2",
  "#0d9488", "#16a34a", "#65a30d", "#ca8a04",
  "#d97706", "#ea580c", "#c2410c", "#475569"
];

// Function to generate a color based on a string (name or ID)
const getAvatarColor = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % avatarColors.length;
  return avatarColors[index];
};

export default function ChatContacts({ waMessageContacts }) {

  const dispatch = useDispatch();
  const { selectedUser } = useSelector((state) => state.messages);

  const selectUserChange = (user_id) => {
    if (+selectedUser === +user_id) return;
    dispatch(resetMessagesState());
    dispatch(setSelectUser(user_id));
    dispatch(setMessage(""));
    dispatch(setReplyMessage(null));
    dispatch(clearChats())
  };

  return (
    <Box
      sx={{
        width: "100%",
        borderRight: "1px solid #ddd",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        height: "100%", // fill parent container
      }}
    >

      {/* Scrollable Contacts List */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <List disablePadding>
          {
            waMessageContacts.map((user) => {
              const avatarColor = getAvatarColor(user.name + user.wa_contact_id);
              return (
                <ListItem
                  key={user.wa_contact_id}
                  button
                  selected={selectedUser === user.wa_contact_id}
                  onClick={() => selectUserChange(user.wa_contact_id)}
                  sx={{
                    position: "relative",
                    py: 1.1,
                    px: 1.75,
                    cursor: "pointer",
                    borderBottom: "1px solid #f1f5f9",
                    transition: "background-color .15s ease",
                    "&:hover": { bgcolor: "#f8fafc" },
                    // Selected chat: light blue + glowing pill marker on the left
                    "&.Mui-selected, &.Mui-selected:hover": { bgcolor: "#eff6ff" },
                    "&.Mui-selected::before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: "18%",
                      bottom: "18%",
                      width: 4,
                      borderRadius: "0 4px 4px 0",
                      background: "linear-gradient(180deg, #93c5fd, #2563eb)",
                      boxShadow: "0 0 10px rgba(37,99,235,0.6)",
                    },
                  }}
                >
                  <ListItemAvatar>
                    {user.unread > 0 ? (
                      <Badge
                        badgeContent={user.unread}
                        color="error"
                        sx={{ "& .MuiBadge-badge": { fontWeight: 800, fontSize: 10.5, border: "2px solid #fff" } }}
                      >
                        <Avatar sx={{ width: 40, height: 40, fontSize: 16, fontWeight: 800, color: avatarColor, bgcolor: `${avatarColor}1f` }}>{user.name[0]}</Avatar>
                      </Badge>
                    ) : (
                      <Avatar sx={{ width: 40, height: 40, fontSize: 16, fontWeight: 800, color: avatarColor, bgcolor: `${avatarColor}1f` }}>{user.name[0]}</Avatar>
                    )}
                  </ListItemAvatar>

                  <Box sx={{ ml: 1, display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight={user.unread > 0 ? 800 : 700}
                        noWrap
                        sx={{ maxWidth: "70%", color: "#0f172a", fontSize: 14 }}
                      >
                        {user.name.length > 15
                          ? `${user.name.substring(0, 15)}…`
                          : user.name}
                      </Typography>
                      <Typography variant="caption" sx={{ flexShrink: 0, color: user.unread > 0 ? "#2563eb" : "#94a3b8", fontWeight: user.unread > 0 ? 700 : 500 }}>
                        {formatLatestDate(user.last_message_time)}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", mt: 0.3, fontSize: 13, color: user.unread > 0 ? "#334155" : "#64748b", fontWeight: user.unread > 0 ? 600 : 400 }}
                    >
                      {/* {user.last_message.toString(0,50)} */}
                      {user.last_message?.length > 30
                        ? `${user.last_message?.substring(0, 30)}…`
                        : user?.last_message}
                    </Typography>
                  </Box>
                </ListItem>
              );
            })
          }
        </List>
      </Box>
    </Box>
  );
};